import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, InputBase } from '@mui/material';
import { EmojiEventsOutlined as TrophyIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Contest } from '../../models/types';
import { apiService } from '../../services/api.service';
import { groupService } from '../../services/group.service';
import { BreadcrumbTrail } from '../ui/BreadcrumbTrail';
import { PageHeader } from '../ui/PageHeader';
import { SegmentedControl } from '../ui/SegmentedControl';
import { SurfaceCard } from '../ui/SurfaceCard';
import { EmptyState } from '../ui/EmptyState';
import { ContestListRow, ContestListStatusLabel, getContestListStatus } from './ContestListRow';

type StatusFilter = 'all' | ContestListStatusLabel;
type TypeFilter = 'all' | 'Team' | 'Individual';

const SECTIONS: ContestListStatusLabel[] = ['Active', 'Upcoming', 'Ended'];

// Every challenge across the viewer's groups, split into Active / Upcoming / Ended sections.
export const ChallengesPage: React.FC = () => {
  const { t } = useTranslation('contests');
  const [contests, setContests] = useState<Contest[]>([]);
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [type, setType] = useState<TypeFilter>('all');

  useEffect(() => {
    apiService
      .getContests()
      .then(setContests)
      .catch(err => {
        console.error('Error fetching contests:', err);
        setError(true);
      })
      .finally(() => setLoading(false));

    // Group names only label rows, so the page still works without them.
    groupService
      .getGroups()
      .then(res => {
        const names: Record<string, string> = {};
        for (const g of res.data?.myGroups ?? []) names[g._id] = g.name;
        setGroupNames(names);
      })
      .catch(err => console.error('Error fetching groups:', err));
  }, []);

  const showGroup = new Set(contests.map(c => c.groupId)).size > 1;

  const rows = useMemo(() => {
    const now = new Date();
    const q = query.trim().toLowerCase();
    return contests
      .map(contest => ({ contest, status: getContestListStatus(contest, now) }))
      .filter(({ status: s }) => status === 'all' || s.label === status)
      .filter(({ contest }) => type === 'all' || (contest.contestType || 'Team') === type)
      .filter(({ contest }) => !q || contest.name.toLowerCase().includes(q) || (groupNames[contest.groupId] ?? '').toLowerCase().includes(q))
      .sort((a, b) => new Date(b.contest.startAt).getTime() - new Date(a.contest.startAt).getTime());
  }, [contests, groupNames, query, status, type]);

  return (
    <>
      <BreadcrumbTrail items={[{ label: t('challenges.breadcrumbGroups'), to: '/groups' }, { label: t('challenges.breadcrumbAll') }]} />
      <PageHeader title={t('challenges.title')} subtitle={t('challenges.subtitle')} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <SegmentedControl<StatusFilter>
            ariaLabel={t('challenges.statusFilter')}
            value={status}
            onChange={setStatus}
            options={[{ value: 'all', label: t('challenges.all') }, ...SECTIONS.map(s => ({ value: s, label: t(`listStatus.${s}`) }))]}
          />
          <SegmentedControl<TypeFilter>
            ariaLabel={t('challenges.typeFilter')}
            value={type}
            onChange={setType}
            options={[
              { value: 'all', label: t('challenges.anyType') },
              { value: 'Team', label: t('contestType.Team') },
              { value: 'Individual', label: t('contestType.Individual') },
            ]}
          />
        </Box>
        <Box
          sx={{
            height: 36, flex: 1, maxWidth: 280, minWidth: 180, display: 'flex', alignItems: 'center', gap: 1, px: 1.25,
            border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--card)', color: 'var(--muted)'
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          <InputBase
            fullWidth
            placeholder={t('challenges.search')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            inputProps={{ 'aria-label': t('challenges.search') }}
            sx={{ fontSize: 14, color: 'var(--fg)' }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center', color: 'var(--danger)', fontSize: 14 }}>{t('challenges.loadError')}</Box>
      ) : rows.length === 0 ? (
        <SurfaceCard bodyPadding={0}>
          <EmptyState
            icon={<TrophyIcon />}
            title={contests.length === 0 ? t('challenges.emptyTitle') : t('challenges.noMatchTitle')}
            description={contests.length === 0 ? t('challenges.emptyDescription') : t('challenges.noMatchDescription')}
          />
        </SurfaceCard>
      ) : (
        SECTIONS.map(section => {
          const items = rows.filter(r => r.status.label === section);
          if (items.length === 0) return null;
          return (
            <SurfaceCard key={section} title={`${t(`challenges.sections.${section}`)} (${items.length})`} bodyPadding={0}>
              {items.map(({ contest, status: s }) => (
                <ContestListRow key={contest._id} contest={contest} status={s} groupName={showGroup ? groupNames[contest.groupId] : undefined} />
              ))}
            </SurfaceCard>
          );
        })
      )}
    </>
  );
};
